import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { worksAPI } from '../services/api';
import { toast } from 'sonner';
import { Upload, FileText, Music as MusicIcon, Video, CheckCircle, AlertCircle, ExternalLink, ArrowLeft } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import BNCoauthorshipModal from '../components/BNCoauthorshipModal';
import AIDeclarationForm from '../components/AIDeclarationForm';

const RegisterWorkPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [uploadedFiles, setUploadedFiles] = useState([]);  // Arquivos (PDF, txt, partitura, video)
  const [audioFile, setAudioFile] = useState(null);  // Arquivo de áudio MP3 separado
  const [publicGallery, setPublicGallery] = useState(false);  // Divulgar na galeria
  const [acceptedTerms, setAcceptedTerms] = useState(false);  // Aceite dos termos
  const [requestBNRegistration, setRequestBNRegistration] = useState(false);  // Solicitar registro na BN
  const [showBNModal, setShowBNModal] = useState(false);  // Modal de coautoria BN
  const [bnCoauthorshipAccepted, setBnCoauthorshipAccepted] = useState(false);  // Aceite coautoria 1%
  
  const [formData, setFormData] = useState({
    title: '',
    work_type: 'musica',
    genre: '',
    lyrics: '',
    duration: '',
    language: 'pt-BR',
    description: '',
    author_cpf: '',  // CPF do autor
    artist_name: '',  // Nome artístico
    coauthors: [],
    request_bn_registration: false,
    // Campos de declaração de IA
    ai_usage: false,
    ai_usage_type: 'none',
    ai_parts_used: [],
    ai_tools_used: '',
    ai_prompt_used: '',
    ai_other_specification: '',
    ai_declaration_accepted: false,
    ai_declaration_date: null
  });

  const [coauthor, setCoauthor] = useState({
    name: '',
    role: 'compositor',
    percentage: 0
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'video/*': ['.mp4', '.mov', '.avi', '.wmv'],
      'text/plain': ['.txt'],
      'image/*': ['.jpg', '.jpeg', '.png']  // Partituras em imagem
    },
    maxFiles: 5,  // Permite até 5 arquivos
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setUploadedFiles(prev => [...prev, ...acceptedFiles]);
        toast.success(`${acceptedFiles.length} arquivo(s) carregado(s) com sucesso!`);
      }
    }
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addCoauthor = () => {
    if (!coauthor.name || coauthor.percentage <= 0) {
      toast.error('Preencha todos os campos do coautor');
      return;
    }

    const totalPercentage = formData.coauthors.reduce((sum, c) => sum + parseFloat(c.percentage), 0);
    if (totalPercentage + parseFloat(coauthor.percentage) > 100) {
      toast.error('A soma das porcentagens nao pode exceder 100%');
      return;
    }

    setFormData({
      ...formData,
      coauthors: [...formData.coauthors, { ...coauthor }]
    });
    setCoauthor({ name: '', role: 'compositor', percentage: 0 });
    toast.success('Coautor adicionado!');
  };

  const removeCoauthor = (index) => {
    setFormData({
      ...formData,
      coauthors: formData.coauthors.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (uploadedFiles.length === 0) {
      toast.error('Por favor, faça upload de pelo menos um arquivo da obra');
      return;
    }

    setLoading(true);

    try {
      // Preparar dados removendo campos vazios
      const cleanedData = { ...formData };
      
      // Remover campos vazios ou converter conforme necessário
      if (!cleanedData.duration || cleanedData.duration === '') {
        delete cleanedData.duration;
      } else {
        // Converter para inteiro se for string
        cleanedData.duration = parseInt(cleanedData.duration);
      }
      
      if (!cleanedData.genre || cleanedData.genre === '') {
        delete cleanedData.genre;
      }
      
      if (!cleanedData.lyrics || cleanedData.lyrics === '') {
        delete cleanedData.lyrics;
      }
      
      if (!cleanedData.description || cleanedData.description === '') {
        delete cleanedData.description;
      }
      
      if (!cleanedData.author_cpf || cleanedData.author_cpf === '') {
        delete cleanedData.author_cpf;
      }
      
      // 1. Criar a obra
      const workResponse = await worksAPI.create(cleanedData);
      const workId = workResponse.data.id;

      // 2. Upload de todos os arquivos
      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        
        // Determinar o tipo de arquivo baseado na extensão
        let fileType = formData.work_type;
        if (file.type.startsWith('audio/')) {
          fileType = 'audio';
        } else if (file.type.startsWith('video/')) {
          fileType = 'video';
        } else if (file.type === 'application/pdf') {
          fileType = 'partitura';
        } else if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
          fileType = 'letra';
        }
        
        await worksAPI.uploadFile(workId, file, fileType);
        toast.success(`Arquivo ${i + 1}/${uploadedFiles.length} enviado`);
      }

      // 2.5. Upload do áudio MP3 (se houver)
      if (audioFile) {
        const formDataAudio = new FormData();
        formDataAudio.append('audio', audioFile);
        
        try {
          console.log('Uploading audio file:', audioFile.name);
          const response = await worksAPI.uploadAudio(workId, formDataAudio);
          console.log('Audio upload response:', response);
          toast.success('Áudio MP3 enviado com sucesso!');
        } catch (audioError) {
          console.error('Erro completo ao enviar áudio:', audioError);
          console.error('Detalhes do erro:', audioError.response?.data);
          toast.error(`Erro ao enviar áudio: ${audioError.response?.data?.detail || audioError.message}`);
          // Não para a execução, continua com o registro
        }
      }

      // 2.6. Atualizar flag public_gallery se marcado
      if (audioFile && publicGallery) {
        try {
          console.log('Atualizando flag public_gallery...');
          await worksAPI.update(workId, { public_gallery: true });
          toast.success('Sua música será divulgada na galeria pública!');
        } catch (galleryError) {
          console.error('Erro ao atualizar galeria:', galleryError);
          toast.warning('Não foi possível marcar para galeria pública');
        }
      }

      toast.success('Obra cadastrada com todos os arquivos! Agora finalize o pagamento.');
      
      // 3. Redirecionar para pagamento
      navigate(`/payment/${workId}`);
    } catch (error) {
      console.error('Erro ao registrar obra:', error);
      
      // Handle validation errors (array) or simple errors (string)
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Erro ao registrar obra';
      
      if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(err => err.msg).join(', ');
      } else if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Informacoes da Obra</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titulo da Obra *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: Deixa no Vacuo"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Obra *
            </label>
            <select
              name="work_type"
              value={formData.work_type}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="musica">Musica</option>
              <option value="letra">Letra</option>
              <option value="partitura">Partitura</option>
              <option value="letra_e_musica">Letra e Musica</option>
              <option value="arranjo">Arranjo</option>
              <option value="producao">Producao</option>
              <option value="video">Video</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Genero Musical
            </label>
            <input
              type="text"
              name="genre"
              value={formData.genre}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: Sertanejo, Pop, Rock"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CPF do Autor Principal *
            </label>
            <input
              type="text"
              name="author_cpf"
              value={formData.author_cpf}
              onChange={(e) => {
                // Formatar CPF automaticamente
                let value = e.target.value.replace(/\D/g, '');
                if (value.length <= 11) {
                  value = value.replace(/(\d{3})(\d)/, '$1.$2');
                  value = value.replace(/(\d{3})(\d)/, '$1.$2');
                  value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                  setFormData({ ...formData, author_cpf: value });
                }
              }}
              maxLength={14}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="000.000.000-00"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              CPF será incluído no certificado para identificação legal
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🎭 Nome Artístico
            </label>
            <input
              type="text"
              name="artist_name"
              value={formData.artist_name}
              onChange={(e) => setFormData({ ...formData, artist_name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: ANTONIO RBNETO"
            />
            <p className="text-xs text-gray-500 mt-1">
              Nome artístico que aparecerá no certificado (opcional). Se não preenchido, usaremos seu nome cadastrado.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duracao (em segundos)
            </label>
            <input
              type="number"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: 155"
            />
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Letra da Musica
          </label>
          <textarea
            name="lyrics"
            value={formData.lyrics}
            onChange={handleChange}
            rows={12}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Cole aqui a letra completa da musica..."
          />
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Observacoes Adicionais
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Informacoes adicionais sobre a obra..."
          />
        </div>

        {/* Declaração de Uso de IA - ECAD 2025 */}
        <div className="mt-8">
          <AIDeclarationForm formData={formData} setFormData={setFormData} />
        </div>
      </div>

      <button
        type="button"
        onClick={() => setStep(2)}
        disabled={formData.ai_usage && !formData.ai_declaration_accepted}
        className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        Proximo: Upload do Arquivo
      </button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Upload dos Arquivos</h3>
        <p className="text-gray-600 mb-2">
          📁 Faça upload dos arquivos da sua música (letra, partitura, vídeo)
        </p>
        <p className="text-sm text-blue-600 mb-6">
          💡 Dica: Você pode enviar múltiplos arquivos! Ex: Letra (PDF/TXT) + Partitura (PDF/IMG) + Vídeo
        </p>
        <p className="text-sm text-orange-600 mb-6">
          🎵 <strong>Atenção:</strong> O áudio MP3 será enviado na próxima etapa!
        </p>

        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
            isDragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }`}
        >
          <input {...getInputProps()} />
          <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          {uploadedFiles.length > 0 ? (
            <div>
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <p className="text-lg font-semibold text-gray-900">{uploadedFiles.length} arquivo(s) carregado(s)</p>
              <p className="text-sm text-gray-600">
                Clique ou arraste para adicionar mais arquivos
              </p>
            </div>
          ) : (
            <div>
              <p className="text-lg font-semibold text-gray-900 mb-2">
                Arraste e solte seus arquivos aqui
              </p>
              <p className="text-gray-600">ou clique para selecionar</p>
              <p className="text-sm text-gray-500 mt-4">
                📄 Letra: PDF, TXT | 🎵 Áudio: MP3, WAV, M4A, OGG | 🎬 Vídeo: MP4, MOV, AVI
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Até 5 arquivos por obra
              </p>
            </div>
          )}
        </div>

        {/* Lista de arquivos enviados */}
        {uploadedFiles.length > 0 && (
          <div className="mt-6 space-y-3">
            <h4 className="font-semibold text-gray-900">Arquivos enviados:</h4>
            {uploadedFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 p-4 rounded-xl">
                <div className="flex items-center space-x-3">
                  {file.type.startsWith('audio/') && <MusicIcon className="w-5 h-5 text-blue-600" />}
                  {file.type.startsWith('video/') && <Video className="w-5 h-5 text-purple-600" />}
                  {file.type === 'application/pdf' && <FileText className="w-5 h-5 text-red-600" />}
                  {file.type === 'text/plain' && <FileText className="w-5 h-5 text-gray-600" />}
                  <div>
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type || 'Desconhecido'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== index))}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Remover
                </button>
              </div>
            ))}
          </div>
        )}

        {uploadedFiles.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Importante:</p>
                <p>Guarde os arquivos originais em local seguro. Qualquer alteração nos arquivos invalidará o certificado.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex space-x-4">
        <button
          type="button"
          onClick={() => setStep(1)}
          className="flex-1 bg-gray-100 text-gray-900 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
        >
          Voltar
        </button>
        <button
          type="button"
          onClick={() => setStep(3)}
          disabled={uploadedFiles.length === 0}
          className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Proximo: Coautores
        </button>
      </div>
    </div>
  );



  // NOVO STEP 3: Upload de Áudio MP3
  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">🎵 Upload de Áudio (MP3)</h3>
        <p className="text-gray-600 mb-6">
          Envie o arquivo de áudio da sua música em formato MP3 (opcional)
        </p>

        {!audioFile ? (
          <div className="border-2 border-dashed border-orange-300 rounded-2xl p-12 text-center bg-orange-50">
            <MusicIcon className="w-16 h-16 text-orange-600 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Enviar Áudio MP3
            </h4>
            <p className="text-gray-600 mb-4">
              Tamanho máximo: 50MB
            </p>
            <input
              type="file"
              accept=".mp3,audio/mpeg"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  if (file.size > 50 * 1024 * 1024) {
                    toast.error('Arquivo muito grande! Máximo 50MB');
                    return;
                  }
                  if (!file.type.includes('audio') && !file.name.endsWith('.mp3')) {
                    toast.error('Apenas arquivos MP3 são aceitos');
                    return;
                  }
                  setAudioFile(file);
                  toast.success('Áudio carregado com sucesso!');
                }
              }}
              className="hidden"
              id="audio-upload"
            />
            <label
              htmlFor="audio-upload"
              className="inline-block bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors cursor-pointer"
            >
              Selecionar Arquivo MP3
            </label>
          </div>
        ) : (
          <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <MusicIcon className="w-12 h-12 text-green-600" />
                <div>
                  <p className="font-semibold text-gray-900">{audioFile.name}</p>
                  <p className="text-sm text-gray-600">
                    {(audioFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setAudioFile(null);
                  toast.info('Áudio removido');
                }}
                className="text-red-600 hover:text-red-700 font-semibold"
              >
                Remover
              </button>
            </div>
          </div>
        )}

        {/* Checkbox para galeria pública */}
        {audioFile && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-6 mt-6">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="public-gallery"
                checked={publicGallery}
                onChange={(e) => setPublicGallery(e.target.checked)}
                className="mt-1 h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="flex-1">
                <label htmlFor="public-gallery" className="font-semibold text-gray-900 cursor-pointer">
                  📻 Divulgar na Galeria Pública
                </label>
                <p className="text-sm text-gray-600 mt-1">
                  Ao marcar esta opção, sua música ficará disponível na galeria pública do site, onde visitantes poderão
                  ouvir o áudio, ler a letra e entrar em contato com você por email.
                </p>
                <p className="text-xs text-blue-600 mt-2">
                  💡 Ótima forma de divulgar seu trabalho e receber feedbacks!
                </p>
              </div>
            </div>
          </div>
        )}

        {!audioFile && (
          <p className="text-sm text-gray-500 text-center mt-4">
            ℹ️ O envio do áudio é opcional. Você pode pular esta etapa se preferir.
          </p>
        )}
      </div>

      <div className="flex space-x-4">
        <button
          type="button"
          onClick={() => setStep(2)}
          className="flex-1 bg-gray-100 text-gray-900 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
        >
          Voltar
        </button>
        <button
          type="button"
          onClick={() => setStep(4)}
          className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
        >
          Próximo
        </button>
      </div>
    </div>
  );

  const renderStep4Coauthors = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Coautores (Opcional)</h3>
        <p className="text-gray-600 mb-6">
          Adicione coautores e defina as porcentagens de participacao
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-6">
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Coautor
              </label>
              <input
                type="text"
                value={coauthor.name}
                onChange={(e) => setCoauthor({ ...coauthor, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Nome completo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Funcao
              </label>
              <select
                value={coauthor.role}
                onChange={(e) => setCoauthor({ ...coauthor, role: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="compositor">Compositor</option>
                <option value="letrista">Letrista</option>
                <option value="arranjador">Arranjador</option>
                <option value="produtor">Produtor</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Porcentagem (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={coauthor.percentage}
                onChange={(e) => setCoauthor({ ...coauthor, percentage: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={addCoauthor}
            className="w-full bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors"
          >
            Adicionar Coautor
          </button>
        </div>

        {formData.coauthors.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Coautores Adicionados:</h4>
            <div className="space-y-3">
              {formData.coauthors.map((c, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="font-semibold text-gray-900">{c.name}</p>
                    <p className="text-sm text-gray-600">{c.role} - {c.percentage}%</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCoauthor(index)}
                    className="text-red-600 hover:text-red-700 font-semibold"
                  >
                    Remover
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-4 text-right">
              <p className="text-sm text-gray-600">
                Total: <span className="font-semibold text-gray-900">
                  {formData.coauthors.reduce((sum, c) => sum + parseFloat(c.percentage), 0)}%
                </span>
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex space-x-4">
        <button
          type="button"
          onClick={() => setStep(3)}
          className="flex-1 bg-gray-100 text-gray-900 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
        >
          Voltar
        </button>
        <button
          type="button"
          onClick={() => setStep(5)}
          className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
        >
          Revisar e Finalizar
        </button>
      </div>
    </div>
  );

  const renderStep5Review = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Revisao Final</h3>
        
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <div>
            <p className="text-sm text-gray-600">Titulo</p>
            <p className="font-semibold text-gray-900">{formData.title}</p>
          </div>

          <div>
            <p className="text-sm text-gray-600">Tipo</p>
            <p className="font-semibold text-gray-900">{formData.work_type}</p>
          </div>

          {formData.genre && (
            <div>
              <p className="text-sm text-gray-600">Genero</p>
              <p className="font-semibold text-gray-900">{formData.genre}</p>
            </div>
          )}

          <div>
            <p className="text-sm text-gray-600">Arquivos ({uploadedFiles.length})</p>
            <div className="space-y-1">
              {uploadedFiles.map((file, i) => (
                <p key={i} className="font-semibold text-gray-900 text-sm">• {file.name}</p>
              ))}
            </div>
          </div>

          {formData.coauthors.length > 0 && (
            <div>
              <p className="text-sm text-gray-600 mb-2">Coautores</p>
              {formData.coauthors.map((c, i) => (
                <p key={i} className="text-gray-900">{c.name} - {c.role} ({c.percentage}%)</p>
              ))}
            </div>
          )}
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mt-6">
          <h4 className="font-semibold text-yellow-900 mb-2">Avisos Importantes:</h4>
          <ul className="space-y-2 text-sm text-yellow-800">
            <li>• Guarde o arquivo original em local seguro</li>
            <li>• Qualquer alteracao no arquivo invalidara o certificado</li>
            <li>• O certificado sera gerado IMEDIATAMENTE</li>
            <li>• Voce recebera um email com o certificado</li>
          </ul>
        </div>
      </div>

      <div className="flex space-x-4">
        <button
          type="button"
          onClick={() => setStep(4)}
          disabled={loading}
          className="flex-1 bg-gray-100 text-gray-900 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          Voltar
        </button>
        <button
          type="button"
          onClick={() => setStep(6)}
          disabled={loading}
          className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          Escolher Plano
        </button>
      </div>
    </div>
  );

  const renderStep6Plan = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Confirmação de Registro</h3>
        <p className="text-gray-600 mb-6">Revise os dados da sua obra e aceite os termos para finalizar</p>
        
        {/* Resumo da Obra */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
          <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <MusicIcon className="w-5 h-5 text-blue-600" />
            SEU PEDIDO - RESUMO DA OBRA
          </h4>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Título da Obra</p>
                <p className="font-bold text-gray-900">"{formData.title}"</p>
              </div>
              
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Tipo</p>
                <p className="font-semibold text-gray-900">{formData.work_type}</p>
              </div>
            </div>

            {formData.genre && (
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Gênero</p>
                <p className="font-semibold text-gray-900">{formData.genre}</p>
              </div>
            )}

            <div className="bg-white rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-2">Autor(es)</p>
              <p className="font-semibold text-gray-900">{user?.name}</p>
              {user?.cpf_cnpj && <p className="text-sm text-gray-600">{user.cpf_cnpj}</p>}
            </div>

            {formData.coauthors.length > 0 && (
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2">Coautores</p>
                {formData.coauthors.map((c, i) => (
                  <div key={i} className="mb-2">
                    <p className="font-semibold text-gray-900">{c.name} - {c.role}</p>
                    <p className="text-sm text-gray-600">Participação: {c.percentage}%</p>
                  </div>
                ))}
              </div>
            )}

            {formData.lyrics && (
              <div className="bg-white rounded-lg p-4 max-h-96 overflow-y-auto">
                <p className="text-sm text-gray-600 mb-2">🎶 LETRA DA OBRA</p>
                <pre className="whitespace-pre-wrap text-sm text-gray-900 font-mono">
                  {formData.lyrics}
                </pre>
              </div>
            )}

            <div className="bg-white rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-2">Arquivos Anexados ({uploadedFiles.length})</p>
              <div className="space-y-1">
                {uploadedFiles.map((file, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <p className="text-sm font-semibold text-gray-900">{file.name}</p>
                    <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(2)} KB)</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Data de Criação</p>
              <p className="font-semibold text-gray-900">{new Date().toLocaleDateString('pt-BR')}</p>
            </div>
          </div>
        </div>

        {/* Termos e Condições */}
        <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
          <h4 className="text-lg font-bold text-gray-900 mb-4">Termos e Condições de Uso</h4>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto text-sm text-gray-700 space-y-2">
            <p className="font-semibold">Ao finalizar o registro, você declara que:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>É o autor ou titular legítimo dos direitos sobre esta obra</li>
              <li>Possui plenos direitos para registrar esta composição</li>
              <li>O conteúdo não viola direitos de terceiros ou leis vigentes</li>
              <li>Todas as informações fornecidas são verdadeiras e precisas</li>
              <li>Compreende que este registro constitui prova privada de autoria</li>
              <li>Concorda com a geração de hash criptográfico (SHA-256) da obra</li>
              <li>Autoriza a emissão do certificado digital de registro</li>
            </ul>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="font-semibold mb-2">Responsabilidades:</p>
              <p className="text-xs">
                Você assume total responsabilidade civil e penal pelo conteúdo registrado. 
                A plataforma atua exclusivamente como prestadora de tecnologia de certificação, 
                não adquirindo direitos sobre a obra. O registro não substitui o registro oficial 
                na Biblioteca Nacional.
              </p>
            </div>
          </div>

          {/* Checkbox de Aceite */}
          <div className="flex items-start gap-3 mb-4">
            <input
              type="checkbox"
              id="acceptTerms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="acceptTerms" className="text-sm text-gray-700 cursor-pointer">
              Li e concordo com os{' '}
              <Link 
                to="/terms" 
                target="_blank"
                className="text-blue-600 hover:underline font-semibold inline-flex items-center gap-1"
              >
                Termos e Condições de Uso
                <ExternalLink className="w-3 h-3" />
              </Link>
              {' '}da plataforma Minha Composição Autoral *
            </label>
          </div>

          {/* Checkbox Registro na Biblioteca Nacional - NOVO MODELO */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-300 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="requestBN"
                checked={requestBNRegistration}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Abrir modal de coautoria
                    setShowBNModal(true);
                  } else {
                    // Desmarcar tudo
                    setRequestBNRegistration(false);
                    setBnCoauthorshipAccepted(false);
                    setFormData({
                      ...formData, 
                      request_bn_registration: false,
                      bn_coauthorship_accepted: false,
                      bn_coauthorship_percentage: 0
                    });
                  }
                }}
                className="mt-1 w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <label htmlFor="requestBN" className="text-sm cursor-pointer">
                <div className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  🏛️ Desejo Registro Oficial na Biblioteca Nacional (Opcional e Gratuito)
                </div>
                <p className="text-gray-700 mb-2">
                  Ao marcar esta opção, você receberá <strong>dupla proteção</strong>: Certificado Digital + Registro Oficial na BN.
                  Será necessário aceitar o <strong>Termo de Coautoria Técnica de 1%</strong> (simbólica, sem valor econômico).
                </p>
                {bnCoauthorshipAccepted && (
                  <div className="bg-green-100 border border-green-300 rounded-lg p-3 mt-2">
                    <p className="text-xs text-green-800 font-semibold">
                      ✅ Termo de Coautoria Técnica ACEITO<br/>
                      • Você: 99% dos direitos<br/>
                      • Minha Composição: 1% coautoria técnica (sem participação financeira)<br/>
                      • Registro na BN em até 120 dias - GRATUITO
                    </p>
                  </div>
                )}
                {!bnCoauthorshipAccepted && requestBNRegistration && (
                  <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3 mt-2">
                    <p className="text-xs text-yellow-800">
                      ⚠️ Aguardando aceite do Termo de Coautoria Técnica
                    </p>
                  </div>
                )}
                <div className="bg-white/60 rounded-lg p-3 mt-2">
                  <p className="text-xs text-gray-600">
                    ✅ <strong>Dupla proteção:</strong> Digital + Oficial<br/>
                    ✅ <strong>Custo:</strong> GRATUITO<br/>
                    ✅ <strong>Coautoria:</strong> 1% simbólica (sem valor econômico)<br/>
                    ✅ <strong>Seus direitos:</strong> 99% + 100% dos rendimentos<br/>
                    ✅ <strong>Prazo:</strong> Registro na BN em até 120 dias
                  </p>
                </div>
              </label>
            </div>
          </div>

          {!acceptedTerms && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                ⚠️ Você precisa aceitar os Termos e Condições para finalizar o registro
              </p>
            </div>
          )}
        </div>

        {/* Avisos Finais */}
        <div className="bg-gray-100 rounded-xl p-4">
          <p className="text-sm text-gray-600 text-center">
            ℹ️ Os seus dados pessoais serão utilizados apenas para apoiar sua experiência 
            neste website e para gerir o acesso à sua conta.
          </p>
        </div>
      </div>

      <div className="flex space-x-4">
        <button
          type="button"
          onClick={() => setStep(4)}
          disabled={loading}
          className="flex-1 bg-gray-100 text-gray-900 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          Voltar
        </button>
        <button
          type="submit"
          disabled={loading || !acceptedTerms}
          className="flex-1 bg-green-600 text-white py-3 rounded-xl font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Processando...' : '✅ Finalizar Registro Autoral'}
        </button>
      </div>
    </div>
  );

  // Handler para aceitar coautoria BN
  const handleAcceptBNCoauthorship = () => {
    setBnCoauthorshipAccepted(true);
    setRequestBNRegistration(true);
    setFormData({
      ...formData,
      request_bn_registration: true,
      bn_coauthorship_accepted: true,
      bn_coauthorship_percentage: 1.0,
      bn_coauthorship_date: new Date().toISOString()
    });
    setShowBNModal(false);
    toast.success('Termo de Coautoria aceito! Sua obra será registrada na BN em até 120 dias.');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          {/* Botão Voltar */}
          <button 
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-6 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Voltar</span>
          </button>
          
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Registrar Nova Obra</h1>
            <p className="text-gray-600">
              Preencha as informacoes e faca o upload para registrar sua musica
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-12">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                    s === step
                      ? 'bg-blue-600 text-white'
                      : s < step
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {s < step ? <CheckCircle className="w-6 h-6" /> : s}
                </div>
                {s < 5 && (
                  <div
                    className={`h-1 w-16 ${
                      s < step ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4Coauthors()}
            {step === 5 && renderStep5Review()}
            {step === 6 && renderStep6Plan()}
          </form>
        </div>
      </div>

      {/* Modal de Coautoria BN */}
      <BNCoauthorshipModal
        isOpen={showBNModal}
        onClose={() => {
          setShowBNModal(false);
          setRequestBNRegistration(false);
        }}
        onAccept={handleAcceptBNCoauthorship}
        workTitle={formData.title || 'Sua Obra'}
        userName={user?.name || 'Usuário'}
        userDetails={user}
      />
    </div>
  );
};

export default RegisterWorkPage;
